/**
 * Subscription Management Component
 * Allows users to view, subscribe to, upgrade, and manage their subscription plans
 */

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../utils/apiUtils';

// Load Stripe (outside component to avoid recreating)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#FFFFFF',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#E83E8C',
      iconColor: '#E83E8C'
    }
  }
};

/**
 * CheckoutForm - Handles payment card input and subscription creation
 */
const CheckoutForm = ({ selectedPrice, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  // Billing details state
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    address: {
      line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    }
  });

  const handleBillingChange = (field, value) => {
    if (field === 'name') {
      setBillingDetails(prev => ({ ...prev, name: value }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate billing details
    if (!billingDetails.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!billingDetails.address.line1.trim()) {
      setError('Please enter your address');
      return;
    }
    if (!billingDetails.address.city.trim()) {
      setError('Please enter your city');
      return;
    }
    if (!billingDetails.address.postal_code.trim()) {
      setError('Please enter your ZIP code');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create subscription and get client secret
      const response = await apiPost('/api/stripe/create-subscription', {
        priceId: selectedPrice.lookup_key || selectedPrice.id,
        email: user.email
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const data = await response.json();

      // If clientSecret is null, subscription might already be active
      if (!data.clientSecret) {
        console.log('Subscription created without client secret (status:', data.status, ')');
        setSuccess(true);
        setProcessing(false);
        // Redirect after showing success message
        setTimeout(() => {
          onSuccess();
        }, 3000);
        return;
      }

      // Confirm payment with billing details
      const { error: confirmError } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: billingDetails.name,
              email: user.email,
              address: billingDetails.address
            }
          }
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
      } else {
        // Success! Show message then redirect
        setSuccess(true);
        setProcessing(false);
        setTimeout(() => {
          onSuccess();
        }, 3000);
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to create subscription');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Billing Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', color: '#FFFFFF' }}>
          Billing Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name on Card"
              value={billingDetails.name}
              onChange={(e) => handleBillingChange('name', e.target.value)}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#FFFFFF'
                },
                '& .MuiInputLabel-root': {
                  color: '#FFFFFF'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={billingDetails.address.line1}
              onChange={(e) => handleBillingChange('line1', e.target.value)}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#FFFFFF'
                },
                '& .MuiInputLabel-root': {
                  color: '#FFFFFF'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={billingDetails.address.city}
              onChange={(e) => handleBillingChange('city', e.target.value)}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#FFFFFF'
                },
                '& .MuiInputLabel-root': {
                  color: '#FFFFFF'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="State"
              value={billingDetails.address.state}
              onChange={(e) => handleBillingChange('state', e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#FFFFFF'
                },
                '& .MuiInputLabel-root': {
                  color: '#FFFFFF'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={billingDetails.address.postal_code}
              onChange={(e) => handleBillingChange('postal_code', e.target.value)}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#FFFFFF'
                },
                '& .MuiInputLabel-root': {
                  color: '#FFFFFF'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Card Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: '#FFFFFF' }}>
          Card Information
        </Typography>
        <Box
          sx={{
            p: 2,
            border: '1px solid rgba(255, 255, 255, 0.23)',
            borderRadius: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.05)'
          }}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Payment Successful! 🎉
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Your <strong>{selectedPrice.product.name}</strong> subscription is now active.
          </Typography>
          <Typography variant="body2">
            Redirecting to your dashboard...
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing || success}
          fullWidth
        >
          {processing ? <CircularProgress size={24} /> : 'Subscribe'}
        </Button>
        <Button variant="outlined" onClick={onCancel} fullWidth disabled={success}>
          Cancel
        </Button>
      </Box>
    </form>
  );
};

/**
 * PlanCard - Displays a subscription plan with pricing and features
 */
const PlanCard = ({ plan, currentTier, onSelect, isPopular, borderColor }) => {
  const isCurrentPlan = currentTier && currentTier.toLowerCase() === plan.metadata?.tier?.toLowerCase();

  // Custom descriptions based on tier
  const tierDescriptions = {
    basic: 'Perfect for individual sales professionals',
    pro: 'Ideal for small teams running multiple outbound campaigns',
    organization: 'Best fit for lead generation agencies or sales teams'
  };

  const description = tierDescriptions[plan.metadata?.tier?.toLowerCase()] || plan.product.description;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: `2px solid ${borderColor}`,
        boxShadow: isPopular ? 4 : 1,
        bgcolor: 'rgba(0, 149, 255, 0.1)',
        backdropFilter: 'blur(10px)'
      }}
    >
      {isPopular && (
        <Chip
          label="Most Popular"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: '#faa32c',
            color: '#000',
            fontWeight: 'bold'
          }}
        />
      )}
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#FFFFFF' }}>
          {plan.metadata?.tier ? plan.metadata.tier.charAt(0).toUpperCase() + plan.metadata.tier.slice(1) : plan.product.name}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#0095FF' }}>
          ${plan.interval === 'year'
            ? ((plan.unit_amount / 100) / 12).toFixed(2)
            : (plan.unit_amount / 100).toFixed(0)
          }
          <Typography component="span" variant="h6" sx={{ color: '#FFFFFF' }}>
            /month
          </Typography>
        </Typography>
        {plan.interval === 'year' && (
          <Typography variant="caption" sx={{ color: '#FFFFFF', display: 'block', mt: -0.5, mb: 1 }}>
            Billed annually at ${(plan.unit_amount / 100).toFixed(2)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2, minHeight: '60px' }}>
          {description}
        </Typography>

        <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

        <Typography variant="body1" sx={{ mb: 2, color: '#FFFFFF' }}>
          <strong>{plan.product.metadata?.credits_per_month ? Number(plan.product.metadata.credits_per_month).toLocaleString() : '2,000'}</strong> credits/month
        </Typography>

        {isCurrentPlan ? (
          <Button variant="outlined" disabled fullWidth sx={{ color: '#FFFFFF', borderColor: '#FFFFFF' }}>
            Current Plan
          </Button>
        ) : (
          <Button
            variant={isPopular ? 'contained' : 'outlined'}
            onClick={() => onSelect(plan)}
            fullWidth
            sx={{
              color: isPopular ? '#FFFFFF' : '#0095FF',
              borderColor: isPopular ? '#0095FF' : '#0095FF',
              bgcolor: isPopular ? '#0095FF' : 'transparent',
              '&:hover': {
                bgcolor: isPopular ? '#0077CC' : 'rgba(0, 149, 255, 0.1)',
                borderColor: '#0095FF'
              }
            }}
          >
            Select Plan
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * SubscriptionManager - Main component for managing subscriptions
 */
const SubscriptionManager = ({ isOnboarding = false, onCheckoutChange, onSubscriptionSuccess }) => {
  const { user } = useAuth();
  const [prices, setPrices] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [error, setError] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch available prices
      const pricesResponse = await apiGet('/api/stripe/prices');
      if (pricesResponse.ok) {
        const pricesData = await pricesResponse.json();
        setPrices(pricesData.prices || []);
      } else {
        throw new Error('Failed to fetch pricing plans');
      }

      // Fetch current subscription
      const subResponse = await apiGet('/api/stripe/subscription');
      if (subResponse.ok) {
        const subData = await subResponse.json();
        setCurrentSubscription(subData.subscription || null);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      setError(error.message || 'Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (price) => {
    setSelectedPrice(price);
    setShowCheckout(true);
    if (onCheckoutChange) {
      onCheckoutChange(true);
    }
  };

  const handleSubscriptionSuccess = () => {
    setShowCheckout(false);
    setSelectedPrice(null);
    if (onCheckoutChange) {
      onCheckoutChange(false);
    }

    // If onSubscriptionSuccess callback is provided (e.g., from onboarding), call it
    if (onSubscriptionSuccess) {
      onSubscriptionSuccess();
    } else {
      // Otherwise, just refresh data and show success message
      fetchData();
      alert('Subscription successful! Welcome to your new plan.');
    }
  };

  const handleCancelCheckout = () => {
    setShowCheckout(false);
    setSelectedPrice(null);
    if (onCheckoutChange) {
      onCheckoutChange(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (showCheckout && selectedPrice) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold', color: '#FFFFFF' }}>
          Subscribe to {selectedPrice.product.name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 3 }}>
          ${(selectedPrice.unit_amount / 100).toFixed(0)}/{selectedPrice.interval}
        </Typography>
        <Elements stripe={stripePromise}>
          <CheckoutForm
            selectedPrice={selectedPrice}
            onSuccess={handleSubscriptionSuccess}
            onCancel={handleCancelCheckout}
          />
        </Elements>
      </Box>
    );
  }

  // Group prices by monthly/yearly and filter to only show Basic, Pro, Organization
  const allowedTiers = ['basic', 'pro', 'organization'];
  const tierOrder = { basic: 0, pro: 1, organization: 2 };

  // Wyspur brand colors for borders
  const tierColors = {
    free: '#808080',        // Grey
    basic: '#0095FF',       // Blue
    pro: '#faa32c',         // Orange/Yellow
    organization: '#0095FF', // Blue
    enterprise: '#FF4081'   // Pink
  };

  // Filter prices based on selected billing period
  const selectedPrices = prices
    .filter(p =>
      p.interval === (billingPeriod === 'monthly' ? 'month' : 'year') &&
      allowedTiers.includes(p.metadata?.tier?.toLowerCase())
    )
    .sort((a, b) => {
      const tierA = tierOrder[a.metadata?.tier?.toLowerCase()] ?? 999;
      const tierB = tierOrder[b.metadata?.tier?.toLowerCase()] ?? 999;
      return tierA - tierB;
    });

  // Free tier card data
  const freeTierCard = {
    product: { name: 'Free' },
    unit_amount: 0,
    interval: 'month',
    metadata: { tier: 'free', credits_per_month: '100' }
  };

  // Enterprise card data
  const enterpriseCard = {
    product: { name: 'Enterprise' },
    metadata: { tier: 'enterprise' }
  };

  const isFreeTier = !currentSubscription || currentSubscription.tier === 'free' || currentSubscription.status !== 'active';

  return (
    <Box sx={{ p: isOnboarding ? 0 : 3 }}>
      {!isOnboarding && (
        <>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary' }}>
            Manage Your Subscription
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Select the plan that best fits your needs
          </Typography>
        </>
      )}

      {!isOnboarding && currentSubscription && currentSubscription.status === 'active' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are currently on the <strong>{currentSubscription.tier?.toUpperCase() || 'FREE'}</strong> plan
        </Alert>
      )}

      {/* Billing Period Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={billingPeriod}
          exclusive
          onChange={(e, newPeriod) => {
            if (newPeriod !== null) {
              setBillingPeriod(newPeriod);
            }
          }}
          sx={{
            '& .MuiToggleButton-root': {
              px: 3,
              py: 1,
              color: '#FFFFFF',
              borderColor: '#0095FF',
              bgcolor: 'rgba(0, 149, 255, 0.1)',
              '&.Mui-selected': {
                backgroundColor: '#0095FF',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#0077CC'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 149, 255, 0.2)'
              }
            }
          }}
        >
          <ToggleButton value="monthly">
            Monthly
          </ToggleButton>
          <ToggleButton value="yearly">
            Yearly
            <Chip
              label="SAVE 20%"
              size="small"
              sx={{
                ml: 1,
                backgroundColor: '#faa32c',
                color: '#000',
                fontWeight: 'bold',
                height: '20px'
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Plan Cards */}
      <Box sx={{
        overflowX: { xs: 'auto', md: 'visible' },
        pb: 2,
        mx: -2,
        px: 2
      }}>
        <Grid container spacing={2} sx={{
          flexWrap: 'nowrap',
          justifyContent: { xs: 'flex-start', md: 'center' },
          minWidth: { xs: 'max-content', md: 'auto' }
        }}>
        {/* Free Tier - Only show in non-onboarding mode */}
        {!isOnboarding && (
          <Grid item sx={{ minWidth: { xs: '220px', sm: '240px', md: 'auto' }, flex: '1 1 0' }}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `2px solid ${tierColors.free}`,
                boxShadow: 1,
                bgcolor: 'background.paper'
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                  Free
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                  $0
                  <Typography component="span" variant="h6" color="text.secondary">
                    /month
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '60px' }}>
                  Get started with basic filtering capabilities
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
                  <strong>100</strong> credits/month
                </Typography>

                <Button variant="outlined" disabled fullWidth>
                  {isFreeTier ? 'Current Plan' : 'Downgrade'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Paid Plans */}
        {selectedPrices.map((price) => (
          <Grid item key={price.id} sx={{
            minWidth: { xs: '220px', sm: '240px', md: isOnboarding ? '220px' : 'auto' },
            flex: isOnboarding ? '0 1 240px' : '1 1 0',
            maxWidth: isOnboarding ? '260px' : 'none'
          }}>
            <PlanCard
              plan={price}
              currentTier={currentSubscription?.tier}
              onSelect={handleSelectPlan}
              isPopular={price.metadata?.tier === 'pro'}
              borderColor={tierColors[price.metadata?.tier?.toLowerCase()] || '#3C3C5A'}
            />
          </Grid>
        ))}

        {/* Enterprise Tier */}
        <Grid item sx={{
          minWidth: { xs: '220px', sm: '240px', md: isOnboarding ? '220px' : 'auto' },
          flex: isOnboarding ? '0 1 240px' : '1 1 0',
          maxWidth: isOnboarding ? '260px' : 'none'
        }}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: `2px solid ${tierColors.enterprise}`,
              boxShadow: 1,
              bgcolor: 'rgba(0, 149, 255, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#FFFFFF' }}>
                Enterprise
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, color: '#0095FF' }}>
                Custom
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF', mb: 2, minHeight: '60px' }}>
                For bespoke plans tailored to your needs
              </Typography>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

              <Typography variant="body1" sx={{ mb: 2, color: '#FFFFFF' }}>
                <strong>Custom</strong> credits/month
              </Typography>

              <Button
                variant="outlined"
                fullWidth
                href="https://calendly.com/wyspur/demo"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#0095FF',
                  borderColor: '#0095FF',
                  '&:hover': {
                    bgcolor: 'rgba(0, 149, 255, 0.1)',
                    borderColor: '#0095FF'
                  }
                }}
              >
                CONTACT SALES
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default SubscriptionManager;
